import {
  useCourierTemplates,
  useCourierTemplateVariables,
} from '@commenda/api';
import {
  Alert,
  Badge,
  Button,
  FieldWrapper,
  Icons,
  Input,
  Label,
  MultiSelect,
  RadioGroup,
  RadioGroupCardItem,
  Separator,
  SingleCombobox,
} from '@commenda/kakashi';
import {
  FieldRefSource,
  SysEmailRecipientMode,
  TEmailNotificationConfig,
  TValueProvider,
} from '@commenda/types';
import { useState } from 'react';
import { useWorkflow } from '../../context/WorkflowContext';
import { ValueProviderEditor } from '../editors/ValueProviderEditor';
import { getDefaultConstObj } from '../editors/helpers';
import { CourierTemplatePreviewModal } from '../modals/CourierTemplatePreviewModal';
import { useSysEmailStepVariables } from './useSysEmailStepVariables';

export type TEmailConfig = TEmailNotificationConfig & {
  recipientMode?: SysEmailRecipientMode;
};

export const EmailConfigFields = ({
  fieldsKey,
  ownerStepId,
  config,
  onChange,
}: {
  fieldsKey: string;
  ownerStepId: string;
  config: TEmailConfig;
  onChange: (updates: Partial<TEmailConfig>) => void;
}) => {
  const { workflow } = useWorkflow();

  const {
    data: templatesData,
    isLoading: isLoadingTemplates,
    error: templatesError,
  } = useCourierTemplates();

  const {
    data: variablesData,
    isLoading: isLoadingVariables,
    error: variablesError,
  } = useCourierTemplateVariables(config.templateId || undefined);

  const [previewOpen, setPreviewOpen] = useState(false);

  // Stable per-row keys so editing/removing a recipient doesn't reuse the wrong row.
  const [recipientKeys, setRecipientKeys] = useState<string[]>(() =>
    (config.recipients ?? []).map(() => crypto.randomUUID())
  );

  const parsedVariables = variablesData?.variables ?? [];

  const {
    seedKey,
    initialSelectedTemplateVars,
    selectedTemplateVars,
    templateValues,
    customRows,
    onSelectTemplateVars,
    setTemplateValue,
    addCustomRow,
    setCustomName,
    setCustomValue,
    removeCustomRow,
  } = useSysEmailStepVariables({
    stepId: fieldsKey,
    templateId: config.templateId,
    contentValueMapping: config.contentValueMapping,
    parsedVariables,
    isLoadingVariables,
    onChange: (contentValueMapping) => onChange({ contentValueMapping }),
  });

  const recipientMode = config.recipientMode ?? SysEmailRecipientMode.MANUAL;
  const recipients = config.recipients ?? [];
  const precedingSteps = workflow?.steps.filter(
    (step) => step.id !== ownerStepId
  );
  const bodyUnavailable =
    !!variablesData && variablesData.bodyAvailable === false;
  const selectedTemplateName = templatesData?.templates.find(
    (template) => template.id === config.templateId
  )?.name;

  // Template-drift: saved variable mappings whose keys are no longer in the (readable) template.
  // They'll be sent but ignored — a signal the template was edited in Courier since this was set up.
  const driftedKeys = variablesData?.bodyAvailable
    ? Object.keys(config.contentValueMapping ?? {}).filter(
        (key) => !parsedVariables.includes(key)
      )
    : [];

  // A step field-ref without a stepId resolves to nothing at runtime (blank
  // variable / missing recipient) — surface it instead of rendering silently.
  const isIncompleteStepRef = (provider: TValueProvider) =>
    provider.type === 'field' &&
    provider.ref.source === FieldRefSource.STEP &&
    !provider.ref.stepId;
  const incompleteRefNames = [
    ...(recipientMode === SysEmailRecipientMode.MANUAL
      ? recipients.map((recipient, index) =>
          isIncompleteStepRef(recipient) ? `recipient ${index + 1}` : null
        )
      : []),
    ...Object.entries(config.contentValueMapping ?? {}).map(
      ([key, provider]) => (isIncompleteStepRef(provider) ? key : null)
    ),
  ].filter((name): name is string => name !== null);

  const onTemplateChange = (value: string) => {
    if (
      value !== config.templateId &&
      Object.keys(config.contentValueMapping ?? {}).length > 0 &&
      !window.confirm(
        'Switching templates clears your variable mappings — continue?'
      )
    ) {
      return;
    }
    onChange({ templateId: value, contentValueMapping: {} });
  };

  const updateRecipient = (index: number, recipient: TValueProvider) => {
    onChange({
      recipients: recipients.map((existing, i) =>
        i === index ? recipient : existing
      ),
    });
  };

  const onRecipientModeChange = (value: string) => {
    const nextMode = value as SysEmailRecipientMode;
    onChange({
      recipientMode: nextMode,
      ...(nextMode === SysEmailRecipientMode.MATTER_THREAD
        ? { recipients: [] }
        : {}),
    });
  };

  const addRecipient = () => {
    onChange({ recipients: [...recipients, getDefaultConstObj()] });
    setRecipientKeys([...recipientKeys, crypto.randomUUID()]);
  };

  const removeRecipient = (index: number) => {
    onChange({ recipients: recipients.filter((_, i) => i !== index) });
    setRecipientKeys(recipientKeys.filter((_, i) => i !== index));
  };

  const recipientRows = recipients.map((recipient, index) => ({
    rowKey: recipientKeys[index] ?? `${index}`,
    recipient,
    index,
  }));

  return (
    <div className="flex flex-col gap-4">
      {incompleteRefNames.length > 0 && (
        <span className="text-text-error txt-medium-regular">
          No source step selected for: {incompleteRefNames.join(', ')}. These
          values won't resolve when the workflow runs — pick a Step ID for each.
        </span>
      )}
      <FieldWrapper>
        <Label>Template</Label>
        {templatesError ? (
          <span className="text-text-error txt-medium-regular">
            Could not load templates.
          </span>
        ) : (
          <SingleCombobox
            options={(templatesData?.templates ?? []).map((template) => ({
              value: template.id,
              label: template.name,
            }))}
            value={config.templateId || ''}
            setValue={onTemplateChange}
            placeholder={
              isLoadingTemplates ? 'Loading templates...' : 'Select a template'
            }
            searchPlaceholder="Search templates"
            notFoundText="No templates found"
            disabled={isLoadingTemplates}
          />
        )}
        {config.templateId && (
          <Button
            variant="link"
            size="sm"
            className="self-start"
            onClick={() => setPreviewOpen(true)}
          >
            View template
          </Button>
        )}
        <Alert
          variant="warning"
          title="Template not listed?"
          description="Only published Courier templates appear here. Publish it in Courier, then reopen this panel."
        />
      </FieldWrapper>

      <Separator />

      <FieldWrapper>
        <Label>Send to</Label>
        <RadioGroup
          className="flex-col"
          value={recipientMode}
          onValueChange={onRecipientModeChange}
        >
          <RadioGroupCardItem
            value={SysEmailRecipientMode.MATTER_THREAD}
            id={`${fieldsKey}-matter-thread`}
          >
            <div className="flex flex-col gap-1">
              <span className="txt-medium-medium">
                People on the matter thread
              </span>
              <span className="txt-small-regular text-text-secondary">
                Emails everyone subscribed to this service request&apos;s thread
                (clients, agents and Commenda admins). If nobody is subscribed,
                falls back to the request&apos;s assignees, then company admins.
              </span>
            </div>
          </RadioGroupCardItem>
          <RadioGroupCardItem
            value={SysEmailRecipientMode.MANUAL}
            id={`${fieldsKey}-specific-addresses`}
          >
            <div className="flex flex-col gap-1">
              <span className="txt-medium-medium">Specific addresses</span>
              <span className="txt-small-regular text-text-secondary">
                Choose the addresses to email.
              </span>
            </div>
          </RadioGroupCardItem>
        </RadioGroup>
        {recipientMode === SysEmailRecipientMode.MANUAL && (
          <>
            <Label className="flex items-center justify-between">
              <span className="grow">Recipients</span>
              <Button size="sm" onClick={addRecipient}>
                <Icons.Plus className="size-4" />
              </Button>
            </Label>
            <div className="flex flex-col gap-2">
              {recipients.length === 0 && (
                <span className="text-text-secondary txt-medium-regular">
                  Add at least one recipient.
                </span>
              )}
              {recipientRows.map(({ rowKey, recipient, index }) => (
                <div key={rowKey} className="flex items-start gap-2">
                  <div className="grow">
                    <ValueProviderEditor
                      value={recipient}
                      onChange={(next) => updateRecipient(index, next)}
                      steps={precedingSteps ?? []}
                      dataSources={[]}
                    />
                  </div>
                  <Icons.Trash02
                    className="size-4 mt-2 cursor-pointer"
                    onClick={() => removeRecipient(index)}
                  />
                </div>
              ))}
            </div>
          </>
        )}
      </FieldWrapper>

      <Separator />

      <FieldWrapper>
        <Label className="flex items-center justify-between">
          <span className="grow">Template variables</span>
          <Button
            size="sm"
            onClick={addCustomRow}
            disabled={!config.templateId}
          >
            <Icons.Plus className="size-4" />
          </Button>
        </Label>

        {!config.templateId ? (
          <span className="text-text-secondary txt-medium-regular">
            Select a template first.
          </span>
        ) : variablesError ? (
          <span className="text-text-error txt-medium-regular">
            Could not read template variables. Add them manually below.
          </span>
        ) : isLoadingVariables ? (
          <span className="text-text-secondary txt-medium-regular">
            Loading variables...
          </span>
        ) : (
          <div className="flex flex-col gap-4">
            <MultiSelect
              key={seedKey}
              options={parsedVariables.map((name) => ({
                value: name,
                label: name,
              }))}
              defaultValue={initialSelectedTemplateVars}
              onValueChange={onSelectTemplateVars}
              placeholder={
                parsedVariables.length
                  ? 'Select variables to map'
                  : 'No variables detected'
              }
              searchText="Search variables"
              selectAllText="Select all"
              notFoundText="No variables found"
            />

            {driftedKeys.length > 0 && (
              <span className="text-text-error txt-medium-regular">
                These mapped variables are no longer in the template (it may
                have changed in Courier): {driftedKeys.join(', ')}. They'll be
                ignored — remove them below, or re-check the template.
              </span>
            )}

            {bodyUnavailable && (
              <span className="text-text-secondary txt-medium-regular">
                We couldn't read this template's variables automatically (it
                uses a custom layout). Open it in Courier, check the variables
                it uses, and add them with the + button.
              </span>
            )}

            {selectedTemplateVars.map((name) => (
              <div
                key={name}
                className="flex flex-col gap-2 rounded-md border border-stroke-3 bg-bg-secondary p-3"
              >
                <div className="flex items-center gap-2">
                  <span className="grow txt-medium-medium">{name}</span>
                  <Badge variant="gray" size="sm">
                    Template
                  </Badge>
                </div>
                <ValueProviderEditor
                  value={templateValues[name] ?? getDefaultConstObj()}
                  onChange={(next) => setTemplateValue(name, next)}
                  steps={precedingSteps ?? []}
                  dataSources={[]}
                />
              </div>
            ))}

            {customRows.map((row) => (
              <div
                key={row.id}
                className="flex flex-col gap-2 rounded-md border border-stroke-3 bg-bg-secondary p-3"
              >
                <div className="flex items-center gap-2">
                  <Input
                    className="grow"
                    placeholder="Variable name"
                    value={row.name}
                    onChange={(e) => setCustomName(row.id, e.target.value)}
                  />
                  <Badge variant="gray" size="sm">
                    Custom
                  </Badge>
                  <Icons.Trash02
                    className="size-4 cursor-pointer"
                    onClick={() => removeCustomRow(row.id)}
                  />
                </div>
                <ValueProviderEditor
                  value={row.value}
                  onChange={(next) => setCustomValue(row.id, next)}
                  steps={precedingSteps ?? []}
                  dataSources={[]}
                />
              </div>
            ))}

            {selectedTemplateVars.length === 0 &&
              customRows.length === 0 &&
              !bodyUnavailable && (
                <span className="text-text-secondary txt-medium-regular">
                  Select variables above to map values, or use + to add a custom
                  one.
                </span>
              )}
          </div>
        )}
      </FieldWrapper>

      {config.templateId && (
        <CourierTemplatePreviewModal
          isOpen={previewOpen}
          onClose={() => setPreviewOpen(false)}
          templateId={config.templateId}
          templateName={selectedTemplateName}
        />
      )}
    </div>
  );
};
